"use client";

import { useEffect, useRef, type ReactNode } from "react";
import {
  POIDetails,
  useIsPOIDetailsOpen,
} from "@/features/items/components/details/POIDetails";
import { usePublishSidebarInset } from "@/features/map/hooks/useSidebarInset";
import { useSidebar } from "@/hooks/useSidebar";
import { cn } from "@/lib/cn";
import { Headline } from "./Headline";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarTabs } from "./SidebarTabs";
import { useProjectConfigs } from "@/features/project/ProjectContext";

/**
 * The results panel beside the map.
 *
 * Collapses by sliding out on a negative margin rather than by unmounting or
 * shrinking: the panel keeps its width while it moves, so its contents don't
 * reflow through the animation, and scroll position and in-flight queries survive.
 *
 * Its width is proportional on wide screens — a fixed 400px panel next to a 2560px
 * map is a sliver — with fixed bounds at either end.
 *
 * `inert` while collapsed keeps the off-screen contents out of the tab order and
 * the accessibility tree; being off-screen alone leaves them reachable.
 *
 * The legacy classes were `transition-margin` / `transition-max-width`, which
 * aren't real Tailwind utilities, so the panel never actually animated. The
 * arbitrary-property form does what was intended — and `isAnimated` withholds it
 * until the panel is moved, so where it starts is not something it slides into.
 *
 * It measures itself and publishes how much of the map it covers, which is what
 * keeps the camera framing its results in the part of the map still visible. The
 * panel is the thing that occludes, so the panel is what gets measured — the
 * legacy app measured it from the outside, by id, from two different places.
 */
export function Sidebar({ children }: { children?: ReactNode }) {
  const { isOpen, isAnimated, open } = useSidebar();
  const isPOIOpen = useIsPOIDetailsOpen();
  const configs = useProjectConfigs();
  const ref = useRef<HTMLDivElement>(null);

  usePublishSidebarInset(ref, isOpen);

  useEffect(() => {
    if (isPOIOpen && !isOpen && configs.isPOIPageEnabled !== false) {
      open();
    }
  }, [isOpen, isPOIOpen, open, configs]);

  return (
    <div
      ref={ref}
      inert={!isOpen}
      className={cn(
        "pointer-events-auto z-20 flex h-full min-h-0 w-0 min-w-0 shrink-0",
        "flex-col bg-white dark:bg-dark-color overflow-hidden",
        "tablet:w-[400px] xl:w-[33.3333%] wide:w-[700px]",
        !isOpen && "-ml-[400px] xl:-ml-[33.3333%] wide:-ml-[700px]",
        isAnimated && "transition-[margin] duration-300 ease-in-out"
      )}
    >
      {/* The details take the whole panel, header and headline included — the
          POI is what the user is looking at, not a section of the results. */}
      {isPOIOpen ? (
        <POIDetails />
      ) : (
        <>
          <SidebarHeader />
          <Headline />
          <SidebarTabs>{children}</SidebarTabs>
        </>
      )}
    </div>
  );
}
